import React, { useEffect, useState } from "react";
import successImg from "./../../assets/images/success.svg";
import MultipleOption from "../../components/webForm/MultipleOption";
import InputStyledComp from "../../components/webForm/InputStyledComp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faUpload,
  faLeftLong,
  faRightLong,
} from "@fortawesome/free-solid-svg-icons";
import PhoneInput from "react-phone-input-2";
import { useParams } from "react-router-dom";
import {
  addWebFormResponse,
  checkPaymentStatus,
  getPaymentLink,
  getWebFormView,
  uploadFile,
} from "../../services/formService";
import toast, { Toaster } from "react-hot-toast";
import { DatePicker, Modal } from "rsuite";
import { Spin } from "antd";
import PaymentFiels from "../../components/webForm/PaymentFiels";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Logger } from '../../utils/logger';
import { BUCKET_URL } from '../../config/config';
import styled from "styled-components";
import { validateFormField } from "../../services/formValidation";
import { useDraftSaving } from "../../hooks/useDraftSaving";
import { useFormNavigation } from "../../hooks/useFormNavigation";
import { usePaymentHandling } from "../../hooks/usePaymentHandling";
import ProgressBar from "./ProgressBar";

export default function WebForm() {
  const { _id } = useParams();

  const [formId, setFormId] = useState("");
  const [formData, setFormData] = useState<any>({});
  const [formOptions, setFormOptions] = useState<any>([]);
  const [newFormData, setNewFormData] = useState<any>({});
  const [disableButton, setDisableButton] = useState(false);
  const [isPageLoad, setIsPageLoad] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);

  const { webForm } = useSelector((state: any) => {
    return state?.rootReducers;
  });

  const dispatch = useDispatch();

  // Use draft saving hook
  const { saveDraft } = useDraftSaving({
    formUniqueCode: formData?.uniqueCode,
    formData: newFormData,
    formStructure: formData?.form, // Pass form structure for nesting group fields
    debounceMs: 1000
  });

  // Use form navigation hook
  const {
    activeField,
    backStep,
    setActiveField,
    setBackStep,
    validateConditions,
    handleBack,
    navigateNext
  } = useFormNavigation({
    formData,
    newFormData,
    onSubmit: submitForm,
    onFileUpload: getSignUrl,
    onSaveDraft: saveDraft
  });

  // Use payment handling hook
  const {
    isPageLoad: paymentLoading,
    paymentDialog,
    paymentStatus,
    generatePaymentLink,
    checkPayment,
    setPaymentDialog
  } = usePaymentHandling({
    formUniqueCode: formData?.uniqueCode,
    formId,
    activeField,
    formData,
    newFormData,
    setActiveField,
    setBackStep,
    backStep,
    setFormData,
    setNewFormData,
    onNavigateNext: navigateNext
  });

  useEffect(() => {
    if (isSubmit) {
      submitForm();
    }
  }, [newFormData, isSubmit]);

  const extractNumber = (val: string) => {
    const newVal = val.split("_");
    return newVal[1];
  };

  async function submitForm() {
    setDisableButton(true);
    try {
      const { end_screen, ...data } = newFormData;

      Logger.log("data.", data);
      const filteredData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== "")
      );

      // Nest group fields by question and group field name
      const structuredData: any = {};

      if (formData?.form && formData.form.length > 0) {
        formData.form.forEach((question: any, questionIndex: number) => {
          const questionKey = `question_${questionIndex + 1}`;

          question.fields.forEach((field: any) => {
            if (field.type === 'group_field' && field.groupFields) {
              // This is a group field - nest it
              const groupData: any = {};

              field.groupFields.forEach((nestedField: any) => {
                if (filteredData[nestedField.fieldName] !== undefined) {
                  groupData[nestedField.fieldName] = filteredData[nestedField.fieldName];
                }
              });

              // Only add if group has data
              if (Object.keys(groupData).length > 0) {
                if (!structuredData[questionKey]) {
                  structuredData[questionKey] = {};
                }
                structuredData[questionKey][field.fieldName] = groupData;
              }
            } else {
              // Regular field - keep at root level (flat)
              if (filteredData[field.fieldName] !== undefined) {
                structuredData[field.fieldName] = filteredData[field.fieldName];
              }
            }
          });
        });

        // Add any remaining fields not in structure (for safety)
        Object.keys(filteredData).forEach((key) => {
          const isInStructure = formData.form.some((q: any) =>
            q.fields.some((f: any) => {
              if (f.fieldName === key) return true;
              if (f.type === 'group_field' && f.groupFields) {
                return f.groupFields.some((gf: any) => gf.fieldName === key);
              }
              return false;
            })
          );

          if (!isInStructure && !structuredData[key]) {
            structuredData[key] = filteredData[key];
          }
        });
      } else {
        // No form structure - fall back to flat structure
        Object.assign(structuredData, filteredData);
      }

      Logger.log("Response data (nested structure):", structuredData);

      const resp = await addWebFormResponse(formId, structuredData);
      if (resp && resp.status) {
        Logger.log(resp.data.message);
        setDisableButton(false);
        toast.success(resp.data.message);
        // Directly set to next field (success screen) - don't use navigateNext to avoid loop
        setActiveField(activeField + 1);
        setBackStep([...backStep, activeField + 1]);
        // Save draft with isSubmitted = true after successful submission
        await saveDraft(true);

        // Clean up draft response ID from localStorage after successful submission
        if (formData?.uniqueCode) {
          localStorage.removeItem(`draft_response_id_${formData.uniqueCode}`);
          Logger.log("Draft response ID cleaned up after successful submission");
        }
        // dispatch(setWebForm({}));
      } else {
        setDisableButton(false);
        toast.error("Failed to submit form. Please try again.");
        Logger.log("Form submission failed:", resp);
      }
    } catch (err: any) {
      setDisableButton(false);
      const errorMessage = err?.response?.data?.message || err?.message || "An error occurred while submitting the form";
      toast.error(errorMessage);
      Logger.log("Form submission error:", err);
    }
  }
  const handleEditFormValue = (
    step: string,
    type: string,
    keyName: string,
    value: string | boolean
  ) => {
    const updatedForm = formData.form.map((item: any) => {
      if (item.question === step) {
        const updatedFields = item.fields.map((field: any) => {
          if (field.type === type) {
            return {
              ...field,
              [keyName]: value,
            };
          }
          return field;
        });
        return {
          ...item,
          fields: updatedFields,
        };
      }
      return item;
    });
    const updatedFormData: FormData = {
      ...formData,
      form: updatedForm,
    };
    setFormData(updatedFormData);
  };
  const updateOptions = (item: any, option: any) => {
    const checkData = formOptions.filter(
      (i: any) => i.question == item.question
    );
    if (checkData.length == 0) {
      const newData = {
        ...item,
        fields: item.fields.map((i: any, ix: number) => {
          return { ...i, options: option };
        }),
      };
      setFormOptions([...formOptions, newData]);
    } else {
      const newData = {
        ...item,
        fields: item.fields.map((i: any, ix: number) => {
          return { ...i, options: option };
        }),
      };
      Logger.log("newData ===>", newData, formOptions);
      const update = formOptions.map((j: any) => {
        if (j.question == newData.question) {
          return newData;
        } else {
          return j;
        }
      });
      setFormOptions([...update]);
    }
    Logger.log("formOptions ====>", formOptions);
  };

  const getFormDetail = async (id: string) => {
    setIsPageLoad(true);
    try {
      const resp = await getWebFormView(id);

      if (resp && resp.status) {
        const newObject: { [key: string]: string } = {};
        resp?.data?.data.form.forEach((step: any) => {
          step.fields.forEach((field: any) => {
            newObject[field.fieldName] = ""; // Set a default value (e.g., "abc")

            // Initialize group field nested fields
            if (field.type === "group_field" && field.groupFields && Array.isArray(field.groupFields)) {
              field.groupFields.forEach((groupField: any) => {
                newObject[groupField.fieldName] = "";
              });
            }
          });
        });
        setNewFormData(newObject);
        const object = resp?.data?.data;
        if (object && object.form && Array.isArray(object.form)) {
          object.form.forEach((step: any) => {
            if (step.fields && Array.isArray(step.fields)) {
              step.fields.forEach((field: any) => {
                if (field.type === "file_upload") {
                  field.file = []; // Adding a 'file' key with a default value
                }
              });
            }
          });
        }

        setIsPageLoad(false);
        setFormData(object);

        // Update favicon if available
        if (object?.organization?.faviconUrl) {
          const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
          if (favicon) {
            favicon.href = object.organization.faviconUrl;
          }
        }

        // Update page title with form name
        if (object?.name) {
          document.title = object.name;
        }
      }
    } catch (err) {
      setIsPageLoad(false);
    }
  };

  const addFiles = (question: any, addFile: any) => {
    if (addFile) {
      const newData = formData.form.map((i: any, ix: number) => {
        if (i.question == question) {
          return {
            ...i,
            fields: i.fields.map((j: any) => {
              return {
                ...j,
                file: [...j.file, addFile],
              };
            }),
          };
        } else {
          return i;
        }
      });
      setFormData({ ...formData, form: newData });
    }
  };
  const deleteFile = (question: any, index: number) => {
    Logger.log("check delete ====>", question, index);
    const newData = formData.form.map((i: any, ix: number) => {
      if (i.question == question) {
        return {
          ...i,
          fields: i.fields.map((j: any) => {
            return {
              ...j,
              file: j.file.filter((a: any, ax: number) => {
                Logger.log("check delete ====>", a, ax, index);
                return ax != index;
              }),
            };
          }),
        };
      } else {
        return i;
      }
    });
    Logger.log("New  formData", question, formData);
    setFormData({ ...formData, form: newData });
  };
  function getSignUrl(
    files: any,
    fileName: string,
    fieldName: string,
    requestType: string
  ) {
    (async () => {
      setIsPageLoad(true);
      try {
        if (files.length > 0) {
          const uploadedFiles = [];
          for (let i = 0; i < files.length; i++) {
            Logger.log(" ==== files [i] === ", !newFormData[fieldName]);
            if (
              !newFormData[fieldName] ||
              newFormData[fieldName]?.filter(
                (item: any) =>
                  item == `custom-form/${formData.uniqueCode}/${files[i].name}`
              ).length == 0
            ) {
              const file = files[i];
              const fileName = `file_${i}.png`; // Example: You can generate file names dynamically
              const uploadResponse = await uploadFile(file, formId, fileName);
              uploadedFiles.push(uploadResponse.data.fileUrl);
            }
          }
          Logger.log("uploadedFiles ===>", uploadedFiles);

          if (uploadedFiles.length > 0) {
            toast.success("File uploaded successfully");
            handleFormObject(fieldName, uploadedFiles);
            setIsPageLoad(false);
            if (requestType == "submit") {
              // submitForm();
              setIsSubmit(true);
            } else {
              navigateNext(); // Use hook's navigate function
            }
          } else {
            setIsPageLoad(false);
            if (requestType == "submit") {
              submitForm();
            } else {
              navigateNext(); // Use hook's navigate function
            }
          }
        } else {
          setIsPageLoad(false);
          if (requestType == "submit") {
            submitForm();
          } else {
            navigateNext(); // Use hook's navigate function
          }
        }
      } catch (err) {
        setIsPageLoad(false);
      }
    })();
  }
  const handleFormObject = (key: string, value: any, type = "all") => {
    Logger.log("============key====", key, value, type);
    if (type == "multiOption") {
      const updatedFormData = { ...newFormData };
      if (Array.isArray(updatedFormData[key])) {
        const index = updatedFormData[key].indexOf(value);
        if (index !== -1) {
          updatedFormData[key].splice(index, 1); // Remove the value if it exists in the array
        } else {
          updatedFormData[key].push(value); // If value is not present, add it to the array
        }
      } else {
        updatedFormData[key] = [value];
      }
      setNewFormData(updatedFormData);
      // dispatch(setWebForm({ [formId]: updatedFormData }));
    } else {
      const updatedFormData = { ...newFormData };
      if (key in updatedFormData) {
        updatedFormData[key] = value;
      } else {
        updatedFormData[key] = value;
      }

      setNewFormData(updatedFormData);
      // dispatch(setWebForm({ [formId]: updatedFormData }));
      if (key.toLowerCase() === "payment_amount") {
        window.open(value, "_blank");
      }
    }
  };
  const handleDateChange = (fieldName: any, date: any) => {
    const d = new Date(date);
    const formattedDate = `${(d.getMonth() + 1).toString().padStart(2, "0")}-${d
      .getDate()
      .toString()
      .padStart(2, "0")}-${d.getFullYear()}`;
    handleFormObject(fieldName, formattedDate);
  };

  useEffect(() => {
    if (_id) {
      setFormId(_id);

      // Check if this is a payment return (payment hook will handle recovery)
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get("razorpay_payment_link_status");
      const paymentId = params.get("razorpay_payment_id");

      // Only load form if NOT returning from payment (payment hook handles that case)
      if (!(paymentStatus && paymentId && paymentStatus.toUpperCase() === "PAID")) {
        setIsPageLoad(true);
        getFormDetail(_id);
      }
    }
  }, [_id]);

  useEffect(() => {
    if (newFormData) {
      localStorage.setItem("custom_new_form_data", JSON.stringify(newFormData));
      localStorage.setItem("custom_form_data", JSON.stringify(formData));
      localStorage.setItem("custom_form_activefield", `${activeField}`);
      localStorage.setItem("custom_form_backStep", JSON.stringify(backStep));
    }
  }, [newFormData, activeField, formData, backStep]);

  // useEffect(()=>{
  //   if(_id && webForm.formData[_id]){
  //     Logger.log('webForm.formData[formId]',webForm.formData[_id])
  //     setNewFormData(webForm.formData[_id])
  //   }
  // },[webForm.formData,_id])

  Logger.log("newFormData ====>", newFormData);
  return (
    // <div id="main" className="main">
    //     <Header />
    <Root>
      <div className="sigma_logo">
        <h4>{formData.name}</h4>
        <div className="logo_section">
          <img src={formData?.organization?.iconUrl ? `${BUCKET_URL}${formData.organization.iconUrl}` : ''} alt="img" />
          <div>
            <h2>{formData?.organization?.name}</h2>
            {/* <h3>Discription(optional)</h3> */}
          </div>
        </div>
      </div>
      {formData.form && formData.form.length > 0 && (
        <ProgressBar
          active={activeField}
          stepDone={backStep}
          options={formData.form}
        />
      )}
      <Spin size="large" tip="Loading..." spinning={isPageLoad || paymentLoading}>
        <div className="form_box">
          {formData.form
            ?.filter((item: any) => {
              return item.question === `question_${activeField}`;
            })
            .map((i: any, index: number) => {
              Logger.log("i>>>", i);
              return i.fields[0].type == "short_text" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title ">{i.fields[0].displayName}</h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <input
                    type="text"
                    className="input_box"
                    placeholder={i.fields[0].placeholder || `Enter ${i.fields[0].displayName || 'your answer'}`}
                    value={newFormData[i.fields[0].fieldName]}
                    onChange={(e) => {
                      handleFormObject(i.fields[0].fieldName, e.target.value);
                    }}
                  />
                  {/* <input type='text' className='input_box' placeholder='Type your answer here' value={i.fields[0].fieldName} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'fieldName', e.target.value) }} /> */}
                </InputStyledComp>
              ) : i.fields[0].type == "number" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <input
                    type="number"
                    className="input_box"
                    placeholder={i.fields[0].placeholder || `Enter ${i.fields[0].displayName || 'number'}`}
                    value={newFormData[i.fields[0].fieldName]}
                    onChange={(e) => {
                      handleFormObject(i.fields[0].fieldName, e.target.value);
                    }}
                  />
                  {/* <input type='text' className='input_box' placeholder='Type your answer here' value={i.fields[0].fieldName} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'fieldName', e.target.value) }} /> */}
                </InputStyledComp>
              ) : i.fields[0].type.toLowerCase() == "phone" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <PhoneInput
                    value={newFormData[i.fields[0].fieldName] || ""}
                    containerClass="phone_field"
                    inputClass="phone_input_left"
                    country="in"
                    masks={{ fr: "(...) ..-..-..", at: "(....) ...-...." }}
                    onChange={(val, country, e, formattedValue) =>
                      handleFormObject(
                        i.fields[0].fieldName,
                        e?.target?.value?.replace(/[ _-]/g, "")
                      )
                    }
                    preferredCountries={["in"]}
                    buttonStyle={{
                      backgroundColor: "#FFF",
                      borderRightWidth: 0,
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                    }}
                    inputStyle={{
                      backgroundColor: "#FFF",
                      color: "#1e293b",
                      fontSize: "18px",
                      borderTopRightRadius: 10,
                      borderBottomRightRadius: 10,
                    }}
                  />
                </InputStyledComp>
              ) : i.fields[0].type == "email" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName}</h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  {/* <input type='email' className='input_box' placeholder='Type your answer here' value={i.fields[0].fieldName} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'fieldName', e.target.value) }} /> */}
                  <input
                    type="text"
                    className="input_box"
                    placeholder={i.fields[0].placeholder || `Enter ${i.fields[0].displayName || 'your answer'}`}
                    value={newFormData[i.fields[0].fieldName]}
                    onChange={(e) => {
                      handleFormObject(i.fields[0].fieldName, e.target.value);
                    }}
                  />
                </InputStyledComp>
              ) : i.fields[0].type == "file_upload" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <div className="all_files">
                    {i.fields[0].file.map((j: any, jx: number) => {
                      return (
                        <span key={jx}>
                          {j.name}
                          {/* File {jx + 1}{" "} */}
                          <button
                            onClick={() => {
                              deleteFile(i.question, jx);
                            }}
                          >
                            <img src="/assets/img/cancleRed.png" />
                          </button>
                        </span>
                      );
                    })}
                  </div>

                  <div className="dragggable_file">
                    <span>
                      <FontAwesomeIcon icon={faUpload} /> Choose file or drag
                      file here{" "}
                    </span>
                    <input
                      type="file"
                      onChange={(e: any) => {
                        addFiles(i.question, e.target.files[0]);
                      }}
                    />
                    {/* <input type='file' onChange = {(e:any)=>{getSignUrl(e.target.files,i.question,i.fields[0].displayName)}}/> */}
                  </div>
                </InputStyledComp>
              ) : i.fields[0].type == "long_text" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <textarea
                    rows={5} // Specify the number of visible rows
                    cols={50} // Specify the number of visible columns
                    placeholder={i.fields[0].placeholder || `Enter ${i.fields[0].displayName || 'your answer'}`}
                    // value={i.fields[0].fieldName} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'fieldName', e.target.value) }}
                    value={newFormData[i.fields[0].fieldName]}
                    onChange={(e) => {
                      handleFormObject(i.fields[0].fieldName, e.target.value);
                    }}
                  />
                </InputStyledComp>
              ) : i.fields[0].type == "date" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <div className="custome_date_box">
                    <DatePicker
                      format="MM/dd/yyyy"
                      size="lg"
                      appearance="subtle"
                      className="date_input_box"
                      onChange={(date) => {
                        handleDateChange(i.fields[0].fieldName, date);
                      }}
                      style={{ fontSize: "30px", backgroundColor: "none" }}
                    />
                    <input
                      type="text"
                      className="input_box"
                      value={newFormData[i.fields[0].fieldName]}
                      placeholder="Select Date"
                    />
                  </div>
                  {/* <input type="date" name="datePick" className='input_box' placeholder={i.fields[0].placeholder} value={newFormData[i.fields[0].fieldName]} onChange={(e) => { handleFormObject(i.fields[0].fieldName, e.target.value) }} /> */}
                </InputStyledComp>
              ) : i.fields[0].type == "address" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <textarea
                    rows={4} // Specify the number of visible rows
                    cols={50} // Specify the number of visible columns
                    placeholder={i.fields[0].placeholder || `Enter ${i.fields[0].displayName || 'address'}`}
                    // value={i.fields[0].fieldName} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'fieldName', e.target.value) }}
                    value={newFormData[i.fields[0].fieldName]}
                    onChange={(e) => {
                      handleFormObject(i.fields[0].fieldName, e.target.value);
                    }}
                  />
                </InputStyledComp>
              ) : i.fields[0].type == "payment" ? (
                <PaymentFiels>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">
                      {i.fields[0].displayName}{" "}
                      <p className="text_danger">
                        {i.fields[0].required && " *"}
                      </p>
                    </h3>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <h4 className="payment_amount">₹ {i.fields[0].amount}</h4>
                  <button
                    onClick={() => {
                      generatePaymentLink(
                        i.fields[0].fieldName,
                        i.fields[0].amount
                      );
                    }}
                    className="pay_btn"
                  >
                    Pay Now
                  </button>
                </PaymentFiels>
              ) : i.fields[0].type == "end_screen" ? (
                <InputStyledComp key={index}>
                  <h3 className="end_screen_box">{i.fields[0].description} </h3>
                  {/* <input type='text' className='input_box' placeholder={'End screen'} value={i.fields[0].description} onChange={(e) => { handleEditFormValue(i.question, i.fields[0].type, 'description', e.target.value) }} /> */}
                </InputStyledComp>
              ) : i.fields[0].type == "multiple_options" ? (
                <MultipleOption>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    <p className="text_danger">
                      {i.fields[0].required && " *"}
                    </p>
                  </div>
                  <p className="discription">{i.fields[0].description}</p>
                  <div className="mt-3 multi_option">
                    {i.fields[0].options.map((j: any, jx: number) => {
                      return (
                        <button
                          onClick={() => {
                            updateOptions(i, j);
                            if (i.fields[0].multipleSelection) {
                              handleFormObject(
                                i.fields[0].fieldName,
                                j.value,
                                "multiOption"
                              );
                            } else {
                              handleFormObject(
                                i.fields[0].fieldName,
                                j.value,
                                ""
                              );
                            }
                          }}
                          className={
                            Array.isArray(newFormData[i.fields[0].fieldName]) &&
                            newFormData[i.fields[0].fieldName].includes(j.value)
                              ? "active"
                              : (newFormData[i.fields[0].fieldName] === j.value ? "active" : "")
                          }
                        >
                          <span>{jx + 1}</span>
                          {j.value.split("_").join(" ")}
                        </button>
                      );
                    })}
                  </div>
                </MultipleOption>
              ) : i.fields[0].type == "text_display" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                    {i.fields[0].description && (
                      <p className="discription">{i.fields[0].description}</p>
                    )}
                  </div>
                  <div
                    className="text_display_content"
                    style={{
                      whiteSpace: 'pre-wrap',
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      marginTop: '15px',
                      marginBottom: '80px', // Space for navigation buttons
                      lineHeight: '1.6',
                      border: '1px solid #e0e0e0',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: (i.fields[0].textContent || '')
                        .replace(/\n/g, '<br/>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/__(.*?)__/g, '<u>$1</u>')
                    }}
                  />
                </InputStyledComp>
              ) : i.fields[0].type == "group_field" ? (
                <InputStyledComp key={index}>
                  <div className="field_name sr_number">
                    <span>
                      {extractNumber(i.question)}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                    <h3 className="title">{i.fields[0].displayName} </h3>
                  </div>
                  {i.fields[0].description && (
                    <p className="discription">{i.fields[0].description}</p>
                  )}

                  {/* Render all grouped fields */}
                  <div
                    className="group_field_container"
                    style={{
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      marginTop: '15px',
                      border: '1px solid #e0e0e0',
                      marginBottom: '80px'
                    }}
                  >
                    {i.fields[0].groupFields && i.fields[0].groupFields.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {i.fields[0].groupFields.map((field: any, fieldIdx: number) => (
                          <div key={fieldIdx} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label
                              style={{
                                fontSize: '15px',
                                fontWeight: 500,
                                color: '#334155',
                                marginBottom: '4px'
                              }}
                            >
                              {field.displayName}
                              {field.required && <span style={{ color: '#dc2626' }}> *</span>}
                            </label>

                            {/* Render different input types based on field.type */}
                            {field.type === 'short_text' || field.type === 'number' || field.type === 'email' ? (
                              <input
                                type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                                className="input_box"
                                placeholder={field.placeholder || `Enter ${field.displayName}`}
                                value={newFormData[field.fieldName] || ""}
                                onChange={(e) => {
                                  handleFormObject(field.fieldName, e.target.value);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  fontSize: '16px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff',
                                  transition: 'all 0.2s',
                                }}
                              />
                            ) : field.type === 'phone' ? (
                              <PhoneInput
                                value={newFormData[field.fieldName] || ""}
                                containerClass="phone_field"
                                inputClass="phone_input_left"
                                country="in"
                                onChange={(val, country, e, formattedValue) =>
                                  handleFormObject(
                                    field.fieldName,
                                    e?.target?.value?.replace(/[ _-]/g, "")
                                  )
                                }
                                preferredCountries={["in"]}
                                buttonStyle={{
                                  backgroundColor: "#FFF",
                                  borderRightWidth: 0,
                                  borderTopLeftRadius: 10,
                                  borderBottomLeftRadius: 10,
                                }}
                                inputStyle={{
                                  backgroundColor: "#FFF",
                                  color: "#1e293b",
                                  fontSize: "16px",
                                  borderTopRightRadius: 10,
                                  borderBottomRightRadius: 10,
                                  width: '100%'
                                }}
                              />
                            ) : field.type === 'date' ? (
                              <DatePicker
                                format="MM/dd/yyyy"
                                size="lg"
                                appearance="subtle"
                                className="date_input_box"
                                value={newFormData[field.fieldName] ? (() => {
                                  // Parse the stored date string back to Date object
                                  const storedValue = newFormData[field.fieldName];
                                  if (typeof storedValue === 'string' && storedValue.includes('-')) {
                                    const [month, day, year] = storedValue.split('-');
                                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                  }
                                  return storedValue instanceof Date ? storedValue : null;
                                })() : null}
                                onChange={(date) => {
                                  if (date) {
                                    const d = new Date(date);
                                    const formattedDate = `${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}-${d.getFullYear()}`;
                                    handleFormObject(field.fieldName, formattedDate);
                                  }
                                }}
                                style={{
                                  fontSize: "16px",
                                  backgroundColor: "#fff",
                                  width: '100%',
                                  borderRadius: '8px'
                                }}
                              />
                            ) : field.type === 'long_text' ? (
                              <textarea
                                rows={4}
                                placeholder={field.placeholder || `Enter ${field.displayName}`}
                                value={newFormData[field.fieldName] || ""}
                                onChange={(e) => {
                                  handleFormObject(field.fieldName, e.target.value);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  fontSize: '16px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff',
                                  transition: 'all 0.2s',
                                  fontFamily: 'inherit',
                                  resize: 'vertical'
                                }}
                              />
                            ) : field.type === 'multiple_options' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                {field.options?.map((option: any, optIdx: number) => (
                                  <label
                                    key={optIdx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '14px 18px',
                                      border: '2px solid',
                                      borderRadius: '10px',
                                      cursor: 'pointer',
                                      backgroundColor: (field.multipleSelection
                                        ? Array.isArray(newFormData[field.fieldName]) && newFormData[field.fieldName].includes(option.value)
                                        : newFormData[field.fieldName] === option.value)
                                        ? '#dbeafe'
                                        : '#fff',
                                      borderColor: (field.multipleSelection
                                        ? Array.isArray(newFormData[field.fieldName]) && newFormData[field.fieldName].includes(option.value)
                                        : newFormData[field.fieldName] === option.value)
                                        ? '#3b82f6'
                                        : '#e5e7eb',
                                      transition: 'all 0.2s ease',
                                      position: 'relative'
                                    }}
                                  >
                                    <input
                                      type={field.multipleSelection ? 'checkbox' : 'radio'}
                                      name={field.fieldName}
                                      value={option.value}
                                      checked={field.multipleSelection
                                        ? Array.isArray(newFormData[field.fieldName]) && newFormData[field.fieldName].includes(option.value)
                                        : newFormData[field.fieldName] === option.value
                                      }
                                      onChange={(e) => {
                                        if (field.multipleSelection) {
                                          handleFormObject(field.fieldName, option.value, 'multiOption');
                                        } else {
                                          handleFormObject(field.fieldName, option.value);
                                        }
                                      }}
                                      style={{
                                        marginRight: '12px',
                                        cursor: 'pointer',
                                        width: '18px',
                                        height: '18px',
                                        accentColor: '#3b82f6',
                                        flexShrink: 0
                                      }}
                                    />
                                    <span style={{ fontSize: '15px', color: '#111827', fontWeight: '500' }}>
                                      {option.displayName || option.value}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <input
                                type="text"
                                className="input_box"
                                placeholder={field.placeholder || `Enter ${field.displayName}`}
                                value={newFormData[field.fieldName] || ""}
                                onChange={(e) => {
                                  handleFormObject(field.fieldName, e.target.value);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  fontSize: '16px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff',
                                  transition: 'all 0.2s',
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted" style={{ margin: 0, textAlign: 'center', color: '#64748b' }}>
                        <small>No fields configured for this group.</small>
                      </p>
                    )}
                  </div>
                </InputStyledComp>
              ) : (
                <div className="content_box"></div>
              );
            })}
          {(() => {
            const currentField = formData.form?.filter((item: any) => {
              return item.question === `question_${activeField}`;
            })[0];

            // Hide navigation buttons only for end_screen (it's the final screen)
            if (currentField?.fields[0].type === "end_screen") {
              return "";
            }

            // Get custom button text if available, otherwise use default
            const isLastField = activeField == formData?.form?.length - 1;
            const customButtonText = currentField?.fields[0]?.buttonText;
            const defaultButtonText = isLastField ? "Submit" : "Next";
            const buttonText = customButtonText || defaultButtonText;

            // Handler for navigation that auto-fills text_display value
            const handleNavigate = () => {
              if (currentField?.fields[0].type === "text_display") {
                handleFormObject(currentField.fields[0].fieldName, "agreed", "");
                setTimeout(() => validateConditions(), 100);
              } else {
                validateConditions();
              }
            };

            return (
              <div className="action_btns">
                {activeField > 1 && (
                  <button onClick={handleBack}>
                    <FontAwesomeIcon icon={faLeftLong} />
                    Back
                  </button>
                )}
                <button
                  className={`${activeField === 1 && "single"}`}
                  onClick={handleNavigate}
                  disabled={disableButton}
                >
                  {buttonText}{" "}
                  <FontAwesomeIcon icon={faRightLong} />
                </button>
              </div>
            );
          })()}
        </div>
      </Spin>

      <Modal
        size={"sm"}
        open={paymentDialog}
        onClose={() => {}}
        dialogClassName="payment_dialog_box_model"
      >
        <div
          className="dialog_model"
          style={{ background: "#EBF0F4", textAlign: "center" }}
        >
          <h5>Payment Successfully</h5>
          <p>
            Thanks you for your payment{" "}
            {formData?.form?.length &&
              formData?.form[activeField - 1].fields[0]?.amount}{" "}
            INR
          </p>
          <img src={successImg} />
          <p>Please wait ...</p>
          {/* 
          <Modal.Footer>
            <Button
              onClick={() => {validateConditions()}}
              appearance="primary"
              size="lg"
            >
              {activeField == formData?.form?.length - 1
                ? "Submit "
                : "Next "}

            </Button>
          </Modal.Footer> */}
        </div>
      </Modal>

      <Toaster position="top-center" reverseOrder={false} />
    </Root>
    // </div>
  );
}

const Root = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
  padding: clamp(0.5rem, 2vw, 1.5rem);
  max-width: 100%;
  margin: 0 auto;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Gilroy-Regular', 'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;

  /* Mobile-first approach */
  @media (max-width: 480px) {
    padding: 0.75rem;
    background: #ffffff;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    background: #ffffff;
  }
  
  /* Tablet */
  @media (min-width: 769px) and (max-width: 1024px) {
    padding: 1.25rem;
    max-width: 900px;
  }

  h4 {
    font-family: 'Gilroy-Bold', 'Gilroy', sans-serif;
    font-size: clamp(1.5rem, 4vw, 2rem);
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
    letter-spacing: -0.025em;
  }

  .sigma_logo {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: clamp(1rem, 3vw, 2rem);
    background: white;
    border-radius: clamp(12px, 2vw, 16px);
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    margin-bottom: clamp(1rem, 3vw, 2rem);

    /* Small phones */
    @media (max-width: 480px) {
      flex-direction: column;
      text-align: center;
      padding: 1rem;
      gap: 0.75rem;
      border-radius: 12px;
      margin-bottom: 1rem;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      padding: 1.5rem;
      gap: 1rem;
    }

    .logo_section {
      display: flex;
      align-items: center;
      gap: clamp(0.5rem, 2vw, 1rem);

      @media (max-width: 480px) {
        flex-direction: column;
        gap: 0.5rem;
      }

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.75rem;
      }
    }

    img {
      width: auto;
      height: clamp(28px, 5vw, 40px);
      max-height: 50px;
      object-fit: contain;
      border-radius: 8px;

      @media (max-width: 480px) {
        height: 28px;
      }

      @media (max-width: 768px) {
        height: 32px;
      }
    }

    h2 {
      font-family: 'Gilroy-Bold', 'Gilroy', sans-serif;
      font-size: clamp(1.125rem, 3vw, 1.5rem);
      font-weight: 600;
      color: #1e293b;
      margin: 0;
      line-height: 1.4;
    }

    h3 {
      font-size: 0.875rem;
      color: #64748b;
      font-weight: 400;
      margin: 0;
      line-height: 1.5;
    }
  }

  .form_box {
    flex: 1;
    background: white;
    border-radius: clamp(12px, 2vw, 16px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
    min-height: clamp(500px, 70vh, 600px);

    /* Small phones */
    @media (max-width: 480px) {
      border-radius: 12px;
      margin-bottom: 20px;
      min-height: 400px;
    }

    @media (max-width: 768px) {
      border-radius: 12px;
      margin-bottom: 20px;
      min-height: 500px;
    }
    
    /* Landscape phones */
    @media (max-width: 768px) and (orientation: landscape) {
      min-height: 350px;
      margin-bottom: 70px;
    }

    .action_btns {
      position: absolute;
      bottom: clamp(1rem, 3vw, 2rem);
      right: clamp(1rem, 3vw, 2rem);
      display: flex;
      gap: clamp(0.75rem, 2vw, 1rem);
      z-index: 10;

      /* Small phones */
      @media (max-width: 480px) {
        position: absolute;
        bottom: 0;
        left: 1rem;
        right: 1rem;
        background: white;
        padding: 1rem;
        border-top: 1px solid #e2e8f0;
        box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
        border-radius: 8px 8px 0 0;
        gap: 0.75rem;
      }

      @media (max-width: 768px) {
        position: absolute;
        bottom: 0;
        left: 1rem;
        right: 1rem;
        background: white;
        padding: 1rem;
        border-top: 1px solid #e2e8f0;
        box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
        border-radius: 8px 8px 0 0;
      }
      
      /* Landscape phones - keep absolute positioning */
      @media (max-width: 768px) and (orientation: landscape) {
        position: absolute;
        bottom: 1rem;
        right: 1rem;
        background: transparent;
        padding: 0;
        border: none;
        box-shadow: none;
      }

      button {
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        color: white;
        font-size: clamp(0.9rem, 2.5vw, 1rem);
        font-weight: 600;
        padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem);
        border-radius: clamp(8px, 2vw, 10px);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 4px -1px rgb(59 130 246 / 0.5);
        min-height: 44px; /* Touch target size */

        &:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px -1px rgb(59 130 246 / 0.5);
        }

        &:active {
          transform: translateY(0);
        }

        &:disabled {
          background: #cbd5e1;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* Small phones */
        @media (max-width: 480px) {
          flex: 1;
          justify-content: center;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border-radius: 8px;
          min-height: 48px;
          
          &.single {
            width: 100%;
          }
        }

        @media (max-width: 768px) {
          flex: 1;
          justify-content: center;
          padding: 1rem;
          font-size: 1.125rem;
          border-radius: 8px;
          min-height: 48px;
          
          &.single {
            width: 100%;
          }
        }
        
        /* Landscape phones */
        @media (max-width: 768px) and (orientation: landscape) {
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
          min-height: 40px;
          flex: none;
        }

        svg {
          width: clamp(0.875rem, 2vw, 1rem);
          height: clamp(0.875rem, 2vw, 1rem);
          flex-shrink: 0;
        }
      }
    }
  }

  .ant-spin-nested-loading {
    height: 100%;
    .ant-spin-container {
      height: 100%;
    }
  }
`;
